/**
 * Drive a {@link GameInstance} through a known sequence of input frames,
 * headlessly. No canvas, no rAF, no real-time pacing — just (input, update)
 * ticks back-to-back as fast as the CPU allows.
 *
 * This is the engine the server-side replay validator uses. Same module,
 * same seed, same input sequence → same final score. If the simulated
 * score doesn't match what the client claimed, the run is flagged or
 * rejected (`docs/spec-backend.md` §7).
 *
 * The browser loop in `apps/shell/src/runtime/loop.ts` shares the same
 * input → update contract; the only differences are real-time pacing
 * (rAF accumulator) and the call to `render(ctx)` once per rAF.
 *
 * **Determinism contract — same as the cartridge contract:**
 * - `update(dt)` is the only mutator. `dt` is always exactly `1/60`.
 * - No wall-clock reads, no PRNG except via `GameContext.random`.
 * - Invariant: same `(seed, frames)` → same `finalScore` byte-for-byte.
 */
/** Tick duration in seconds. Always 1/60, exact. */
export const TICK_SECONDS = 1 / 60;
/**
 * Reconstitute a per-tick `InputState` stream from a compact frame log
 * + a tick count. The bitmask holds the last-seen state between entries.
 *
 * @param frames Compacted log: each entry's tick is the first tick at
 *   which this bitmask becomes active.
 * @param ticks Total simulation ticks to expand. The validator uses this
 *   independently of the frame log so a malicious payload can't override
 *   the run length.
 */
export function expandFrames(frames, ticks) {
    const out = new Array(ticks);
    let nextEntry = 0;
    let activeMask = 0;
    for (let t = 0; t < ticks; t++) {
        while (nextEntry < frames.length &&
            frames[nextEntry][0] === t) {
            activeMask = frames[nextEntry][1];
            nextEntry++;
        }
        out[t] = inputStateFromBitmask(activeMask);
    }
    return out;
}
/**
 * Decode an `InputState` from a bitmask. Layout matches `input-state.ts`'s
 * `InputBitmask`. Edge events (`justPressed`/`justReleased`) are computed
 * from the previous frame's mask outside this function — see
 * {@link runHeadlessSimulation}.
 */
export function inputStateFromBitmask(mask) {
    return {
        up: (mask & 0b1) !== 0,
        down: (mask & 0b10) !== 0,
        left: (mask & 0b100) !== 0,
        right: (mask & 0b1000) !== 0,
        fireA: (mask & 0b10000) !== 0,
        fireB: (mask & 0b100000) !== 0,
        fireC: (mask & 0b1000000) !== 0,
        start: (mask & 0b10000000) !== 0,
        coin: (mask & 0b100000000) !== 0,
        stickL: { x: 0, y: 0 },
        stickR: { x: 0, y: 0 },
        justPressed: EMPTY_SET,
        justReleased: EMPTY_SET,
    };
}
const EMPTY_SET = Object.freeze(new Set());
/**
 * Run a `GameInstance` for `ticks` ticks, feeding it the expanded input
 * stream. Used by the validator (`packages/core/validators/<slug>.ts`)
 * and by determinism tests.
 *
 * The caller owns instance creation (so they can wire up a custom
 * GameContext for headless mode — typically with a no-op AudioBus and a
 * no-op StorageAPI). See `runReplayAgainstModule` for the higher-level
 * helper that wires those for you.
 */
export function runHeadlessSimulation(instance, frames, ticks) {
    if (ticks < 1) {
        throw new Error('runHeadlessSimulation: ticks must be >= 1');
    }
    const expanded = expandFrames(frames, ticks);
    let prevMask = 0;
    for (let t = 0; t < ticks; t++) {
        const next = expanded[t];
        const currentMask = bitmaskFromInputState(next);
        // Edge-event sets — derived per tick from the bitmask delta.
        const stateWithEdges = {
            ...next,
            justPressed: edgeSet(prevMask, currentMask, true),
            justReleased: edgeSet(prevMask, currentMask, false),
        };
        instance.input(stateWithEdges);
        instance.update(TICK_SECONDS);
        prevMask = currentMask;
    }
    return {
        finalScore: instance.getFinalScore(),
        ticksRun: ticks,
    };
}
/**
 * Re-encode an `InputState`'s digital buttons back to a bitmask for the
 * edge-event computation. Inverse of {@link inputStateFromBitmask}.
 */
function bitmaskFromInputState(s) {
    return ((s.up ? 0b1 : 0) |
        (s.down ? 0b10 : 0) |
        (s.left ? 0b100 : 0) |
        (s.right ? 0b1000 : 0) |
        (s.fireA ? 0b10000 : 0) |
        (s.fireB ? 0b100000 : 0) |
        (s.fireC ? 0b1000000 : 0) |
        (s.start ? 0b10000000 : 0) |
        (s.coin ? 0b100000000 : 0));
}
/**
 * Compute the set of buttons that transitioned (pressed or released) this
 * tick. `pressed=true` returns just-pressed; `pressed=false` returns
 * just-released.
 */
function edgeSet(prev, curr, pressed) {
    const transitions = pressed ? curr & ~prev : prev & ~curr;
    if (transitions === 0) {
        return EMPTY_SET;
    }
    const set = new Set();
    for (const [bit, name] of EDGE_BUTTON_BITS) {
        if ((transitions & bit) !== 0) {
            set.add(name);
        }
    }
    return set;
}
const EDGE_BUTTON_BITS = [
    [0b1, 'up'],
    [0b10, 'down'],
    [0b100, 'left'],
    [0b1000, 'right'],
    [0b10000, 'fireA'],
    [0b100000, 'fireB'],
    [0b1000000, 'fireC'],
    [0b10000000, 'start'],
    [0b100000000, 'coin'],
];
//# sourceMappingURL=headless-loop.js.map